<template>
  <div
    id="content-container"
    class="content-container"
  >
    <div class="sticky top-0 px-6">
      <section class="my-4 flex items-center justify-between text-body-color">
        <div class="flex-col justify-between">
          <p class="mb-2 text-left">
            {{ data?.identity?.legal_name }}
          </p>
          <div class="flex">
            <p
              v-for="id in mainIdentifiers"
              :key="id?.is_type"
              class="paragraph mr-3"
            >
              {{ formatIsType(id?.is_type) }} {{ id?.value }}
            </p>
          </div>
        </div>

        <img
          v-if="brokerLogo"
          class="w-21 h-11"
          :src="brokerLogo"
          alt="broker logo"
        />
      </section>

      <section class="mb-4 mt-9 flex items-center justify-between">
        <h4
          v-if="stepName === 'Insurance-1' && hasFileAuto && !hasFileCargo"
          class="text-lg"
        >
          Cargo
        </h4>
        <h4
          v-else
          class="text-lg"
        >
          {{ stepTypes?.[formPosition]?.title }}
        </h4>
        <p class="paragraph">
          Step {{ evaluateStep }} of {{ evaluateStepTotal }}
        </p>
      </section>
      <p
        v-if="calculateSubTitle"
        class="-mt-5 text-left text-body-color"
      >
        {{ calculateSubTitle }}
      </p>
      <!-- <p
        v-if="stepName?.includes('Questions')"
        class="text-body-color text-left -mt-5"
      >Section {{ stepName.replace('Questions-', '') }}</p> -->
      <progress-indicator
        :current-step="stepTypes?.[formPosition]?.step"
        :auto-skip-stage-six="auto_skip_stage_six"
        :auto-skip-stage-seven="auto_skip_stage_seven"
        :auto-skip-stage-eight="auto_skip_stage_eight"
        :auto-skip-stage-nine="auto_skip_stage_nine"
      />
      <div class="-mt-3 hidden justify-between md:flex">
        <div
          v-for="(title, index) in stepTitles"
          :key="title"
          class="flex justify-center text-gray-over-dark"
        >
          <span class="relative h-3 w-3">
            <p
              class="absolute top-0 text-xxs"
              :class="
                index + 1 <= stepTypes?.[formPosition]?.step
                  ? 'font-bold text-dark-blue'
                  : 'font-medium text-gray-over-dark'
              "
              :style="progressTitleStyles(title)"
            >
              {{ title }}
            </p>
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="mounted"
      class="w-full"
    >
      <form
        novalidate="novalidate"
        @submit.prevent
      >
        <div
          v-for="(step, index) in filteredSteps"
          :key="index"
          class="relative"
        >
          <Transition
            mode="out-in"
            :name="transition_name"
          >
            <component
              :is="step.component"
              v-show="formPosition === index"
              class="absolute inset-0 px-6 py-4 sm:py-6"
              :data="data"
              :update-field="updateField"
              :form-position="formPosition"
              :title="step.title"
              :step-name="stepName"
              :next-step="nextStep"
              :skip-step="skipStep"
              :screen-size="screenSize"
              @exit-modal="emitExitModal"
            />
          </Transition>
        </div>
      </form>
    </div>

    <error-report-modal
      v-if="showErrorModal"
      :step-name="stepName"
      :visible="showErrorModal"
      @close="onCloseModal"
      @success="onSuccessError"
      @toast="openToast"
    />
    <success-modal
      v-if="showSentModal"
      :visible="showSentModal"
      @close="onCloseSentModal"
    >
      Message Sent
    </success-modal>
    <need-help-modal
      v-if="showNeedHelpModal"
      :visible="showNeedHelpModal"
      @close="onCloseNeedHelpModal"
    />
  </div>
</template>

<script>
import { ref, shallowRef, computed } from 'vue';
import { useStore, mapGetters } from 'vuex';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import debounce from 'lodash.debounce';

// import axios from "axios";
import Classifications from '@/components/steps/Identity/Classifications.vue';
import IdentityStepOne from '@/components/steps/Identity/IdentityStepOne.vue';
import IdentityStepTwo from '@/components/steps/Identity/IdentityStepTwo.vue';
import IdentityStepThree from '@/components/steps/Identity/IdentityStepThree.vue';
import IdentityStepFour from '@/components/steps/Identity/IdentityStepFour.vue';
import Operations from '@/components/steps/Operations/Operations.vue';
import Lanes from '@/components/steps/Lanes/Lanes.vue';
import ProgressIndicator from '@/components/ProgressIndicator.vue';
import ButtonMain from '@/components/Button.vue';
import ErrorReportModal from '@/components/modals/ErrorReportModal.vue';
import CertificationsStepOne from '@/components/steps/Certifications/CertificationsStepOne.vue';
import CertificationsStepTwo from '@/components/steps/Certifications/CertificationsStepTwo.vue';
import ContractStepOne from '@/components/steps/Contract/ContractStepOne.vue';
import ContractStepTwo from '@/components/steps/Contract/ContractStepTwo.vue';
import SuccessModal from '@/components/modals/SuccessModal.vue';
import Integrations from '@/components/steps/Integrations/Integrations.vue';
import PaymentStepOne from '@/components/steps/Payment/PaymentStepOne.vue';
import PaymentStepTwo from '@/components/steps/Payment/PaymentStepTwo.vue';
import PaymentStepThree from '@/components/steps/Payment/PaymentStepThree.vue';
import PaymentStepFour from '@/components/steps/Payment/PaymentStepFour.vue';
import PaymentStepFive from '@/components/steps/Payment/PaymentStepFive.vue';
import InsuranceStepOne from '@/components/steps/Insurance/InsuranceStepOne.vue';
import InsuranceStepTwo from '@/components/steps/Insurance/InsuranceStepTwo.vue';
import InsuranceStepThree from '@/components/steps/Insurance/InsuranceStepThree.vue';
import InsuranceStepFour from '@/components/steps/Insurance/InsuranceStepFour.vue';
import Questions from '@/components/steps/Questions/Questions.vue';
import OwnershipStepOne from '@/components/steps/Ownership/OwnershipStepOne.vue';
import OwnershipStepTwo from '@/components/steps/Ownership/OwnershipStepTwo.vue';
// import { toastResponse } from "@/common/handleServerResponse";
import { toastResponseInSetup } from '@/common/handleServerResponse';
import needHelp from '@/mixins/needHelp';
// import { formatErrorResponse } from "@/common/handleServerResponse";
import stepInformation from '@/common/stepInformation';

const classifications = shallowRef(Classifications);
const identityStepOne = shallowRef(IdentityStepOne);
const identityStepTwo = shallowRef(IdentityStepTwo);
const identityStepThree = shallowRef(IdentityStepThree);
const identityStepFour = shallowRef(IdentityStepFour);
const operations = shallowRef(Operations);
const lanes = shallowRef(Lanes);
const certificationsStepOne = shallowRef(CertificationsStepOne);
const certificationsStepTwo = shallowRef(CertificationsStepTwo);
const integrations = shallowRef(Integrations);
const contractStepOne = shallowRef(ContractStepOne);
const contractStepTwo = shallowRef(ContractStepTwo);
const paymentStepOne = shallowRef(PaymentStepOne);
const paymentStepTwo = shallowRef(PaymentStepTwo);
const paymentStepThree = shallowRef(PaymentStepThree);
const paymentStepFour = shallowRef(PaymentStepFour);
const paymentStepFive = shallowRef(PaymentStepFive);
const insuranceStepOne = shallowRef(InsuranceStepOne);
const insuranceStepTwo = shallowRef(InsuranceStepTwo);
const insuranceStepThree = shallowRef(InsuranceStepThree);
const insuranceStepFour = shallowRef(InsuranceStepFour);
const ownershipStepOne = shallowRef(OwnershipStepOne);
const ownershipStepTwo = shallowRef(OwnershipStepTwo);
const questions = shallowRef(Questions);

export default {
  name: 'FormFlow',
  components: {
    ButtonMain,
    ProgressIndicator,
    ErrorReportModal,
    SuccessModal,
  },
  mixins: [needHelp],
  emits: ['exit-modal'],
  data() {
    return {
      mounted: false,
      screenSize:
        window?.innerWidth >= 640
          ? window?.innerWidth <= 1024
            ? 'tablet'
            : 'desktop'
          : 'mobile',
      steps: [
        {
          title: 'Classifications',
          component: classifications,
          name: 'Classifications',
        },
        {
          title: 'Identity Step One',
          component: identityStepOne,
          name: 'Identity',
        },
        {
          title: 'Identity Step Two',
          component: identityStepTwo,
          name: 'Identity',
        },
        {
          title: 'Identity Step Three',
          component: identityStepThree,
          name: 'Identity',
        },
        {
          title: 'Identity Step Four',
          component: identityStepFour,
          name: 'Identity',
        },
        { title: 'Operations', component: operations, name: 'Operations' },
        { title: 'Lanes', component: lanes, name: 'Lanes' },
        {
          title: 'Certifications Step One',
          component: certificationsStepOne,
          name: 'Certifications',
        },
        {
          title: 'Certifications Step Two',
          component: certificationsStepTwo,
          name: 'Certifications',
        },
        {
          title: 'Insurance Step One',
          component: insuranceStepOne,
          name: 'Insurance',
        },
        {
          title: 'Insurance Step Two',
          component: insuranceStepTwo,
          name: 'Insurance',
        },
        {
          title: 'Insurance Step Three',
          component: insuranceStepThree,
          name: 'Insurance',
        },
        {
          title: 'Insurance Step Four',
          component: insuranceStepFour,
          name: 'Insurance',
        },
        {
          title: 'Payment Step One',
          component: paymentStepOne,
          name: 'Payment',
        },
        {
          title: 'Payment Step Two',
          component: paymentStepTwo,
          name: 'Payment',
        },
        {
          title: 'Payment Step Three',
          component: paymentStepThree,
          name: 'Payment',
        },
        {
          title: 'Payment Step Four',
          component: paymentStepFour,
          name: 'Payment',
        },
        {
          title: 'Payment Step Five',
          component: paymentStepFive,
          name: 'Payment',
        },
        {
          title: 'Questions Step One',
          component: questions,
          name: 'Questions',
        },
        {
          title: 'Integrations',
          component: integrations,
          name: 'Integrations',
        },
        {
          title: 'Ownership Step One',
          component: ownershipStepOne,
          name: 'Ownership',
        },
        {
          title: 'Ownership Step Two',
          component: ownershipStepTwo,
          name: 'Ownership',
        },
        {
          title: 'Contract Step One',
          component: contractStepOne,
          name: 'Contract',
        },
        {
          title: 'Contract Step Two',
          component: contractStepTwo,
          name: 'Contract',
        },
      ],
      currentStep: 0,
      temporaryEdit: false,
      error: false,
      dot_value: '',
      mc_value: '',
    };
  },
  async created() {
    const clientId = localStorage.getItem('client_id');
    await this.$store.dispatch('brokers/getLogoByClientId', clientId);

    addEventListener('resize', () => {
      switch (true) {
        case window?.innerWidth <= 640:
          return (this.screenSize = 'mobile');
        case window?.innerWidth <= 1024:
          return (this.screenSize = 'tablet');
        default:
          return (this.screenSize = 'desktop');
      }
    });
  },
  computed: {
    filteredSteps() {
      const filterName = this.shouldEnableCHRFlow
        ? 'Integrations'
        : 'Ownership';
      return this.steps.filter((step) => step.name !== filterName);
    },
    shouldEnableCHRFlow() {
      return this.$store.getters['shouldEnableCHRFlow'];
    },
    transition_name() {
      return this.$store.state.formRoutes?.stepTransitionName || 'slide-fadeup';
    },
    ...mapGetters('insurance', ['getHasFileCargo', 'getHasFileAuto']),
    hasFileCargo() {
      return this.getHasFileCargo;
    },
    hasFileAuto() {
      return this.getHasFileAuto;
    },
    storedPaymentMethods() {
      return this.$store.state.payments?.activePaymentMethods;
    },
    stepTitles() {
      let titles = [
        'Identity',
        'Operations',
        'Lanes',
        'Certifications',
        'Insurance',
      ];
      if (this.auto_skip_stage_six == false) {
        titles.push('Payment');
      }
      if (this.auto_skip_stage_seven == false) {
        titles.push('Questionnaire');
      }
      if (this.auto_skip_stage_eight == false) {
        titles.push(this.shouldEnableCHRFlow ? 'Ownership' : 'Integrations');
      }
      if (this.auto_skip_stage_nine == false) {
        titles.push('Contract');
      }
      return titles;
    },
    firstTime() {
      return this.$store.state.firstTime;
    },
    // Company Type of the user completing the onboarding packet
    companyType() {
      return this.$store.state.users.currentUser?.company?.company_type;
    },
    onboardingPacket() {
      return this.$store.state.onboardingPacket;
    },
    currentSectionName() {
      return this.stepName?.split('-')?.[0];
    },
    calculateSubTitle() {
      if (this.stepName?.includes('Insurance')) {
        return 'Insurance';
      } else if (this.stepName === 'Questions') {
        let currentSection = this.$store.state.questions?.section;
        let sectionNames = this.$store.state.questions?.section_names;
        return sectionNames[currentSection];
      }
      return '';
    },
    latestFactor() {
      return this.$store.state.factors?.latestFactor?.length
        ? this.$store.state.factors?.latestFactor?.[0]
        : null;
    },
    current_tax_form() {
      return this.$store.state.taxQuestions?.current_tax_form || null;
    },
    taxFormTitle() {
      if (this.current_tax_form && this.current_tax_form !== 'w_9') {
        const titles = {
          form_8233: 'Form 8233',
          w_8ben: 'W-8BEN',
          w_8ben_e: 'W-8BEN-E',
          w_8eci: 'W-8ECI',
        };
        return titles[this.current_tax_form];
      } else {
        return null;
      }
    },
    evaluateStep() {
      switch (true) {
        case this.stepName?.includes('Questions'):
          return this.$store.state.questions?.section_index + 1;
        case !this.firstTime['Insurance'] && this.stepName === 'Insurance-3':
        case this.$store.state.carriers.carrierData?.auto_liability_insurance
          ?.id > 0 && this.stepName === 'Insurance-3':
          return '2';
        case (this.$store.state.carriers.carrierData?.workers_comp_selections
          ?.id > 0 ||
          this.$store.state.carriers.carrierData?.auto_liability_insurance?.id >
            0) &&
          this.stepName === 'Insurance-4':
          return '3';
        default:
          return this.stepTypes?.[this.formPosition]?.subStep;
      }
    },
    evaluateStepTotal() {
      switch (true) {
        case !this.firstTime['Contract'] && this.stepName?.includes('Contract'):
          return '2';
        case this.stepName?.includes('Questions'):
          return this.$store.state?.questions?.total_sections;
        case !this.firstTime['Insurance'] &&
          this.stepName?.includes('Insurance'):
        case this.stepName?.includes('Insurance') &&
          this.$store.state.carriers.carrierData?.auto_liability_insurance?.id >
            0:
          return '3';
        case this.stepName?.includes('Identity') &&
          this.skipSubstepsThreeAndFourIdentity:
          return '2';
        case !this.firstTime['Certifications'] &&
          this.stepName === 'Certifications-1':
          return '1';
        case !this.firstTime['Payment'] &&
          this.stepName == 'Payment-2' &&
          this.$store.state.payments?.saved_payment_method?.is_type !== 'check':
          return '2';
        default:
          return this.stepTypes?.[this.formPosition]?.subStepTotal;
      }
    },
    brokerLogo() {
      return (
        this.$store.state.carriers?.carrierData?.connection?.issuer?.logo
          ?.url ||
        this.$store.state.brokers?.logo?.url ||
        null
      );
    },
    skipSubstepsThreeAndFourIdentity() {
      if (
        import.meta.env.VITE_DISABLE_RATE_CON_AND_INVOICING_CONTACT_STEPS == 1
      ) {
        return true;
      }
      return (
        this.$store.state.carriers.carrierDetails?.authority?.authorities
          ?.broker_authority_status !== 'Active'
      );
    },
    auto_skip_stage_six() {
      return (
        this.$store.state.carriers?.carrierData?.auto_skip_stage_six || false
      );
    },
    auto_skip_stage_seven() {
      if (this.$store.state.carriers?.carrierData?.auto_skip_stage_seven) {
        return true;
      } else if (
        this.$store.state.questions?.total_sections == 0 &&
        this.stepName?.includes('Questions')
      ) {
        return true;
      } else {
        return false;
      }
    },
    auto_skip_stage_eight() {
      if (this.shouldEnableCHRFlow) return false;

      return (
        this.$store.state.carriers?.carrierData?.auto_skip_stage_eight || false
      );
    },
    auto_skip_stage_nine() {
      return (
        this.$store.state.carriers?.carrierData?.auto_skip_stage_nine || false
      );
    },
    mainIdentifiers() {
      const mainIdentifiers = ['DOT', 'MC'];
      const identifiers =
        this.data.identity?.identifiers?.filter(
          (el) =>
            mainIdentifiers.includes(el?.is_type) || el?.is_manually_created
        ) ?? [];
      return identifiers;
    },
    latest_company_tax_form_submission() {
      return (
        this.$store.state.taxQuestions?.latest_company_tax_form_submission ||
        null
      );
    },
  },
  props: {
    nextStep: { type: Function },
    backStep: { type: Function },
    skipStep: { type: Function },
    formPosition: Number,
    stepTypes: Array,
    data: Object,
    updateField: { type: Function },
    openSaveModal: { type: Function },
    beginningStep: { type: Function },
    stepName: String,
    // updatePacket: Function,
    buttonLoading: Boolean,
    triggerLoadingBtn: Function,
    handleW8Submit: Function,
  },
  watch: {
    formPosition() {
      this.currentStep = this.formPosition;
      if (this.temporaryEdit) {
        this.temporaryEdit = false;
        let lastStep =
          this.stepTitles[
            this.stepTitles?.indexOf(this.currentSectionName) - 1
          ];
        this.$store.commit('firstTime/updateStepFirstTime', {
          step: lastStep,
          value: false,
        });
        this.$store.commit('firstTime/updateStepFirstTime', {
          step: this.currentSectionName,
          value: false,
        });
      }
      this.$store.commit('onboardingPacket/setViewedStepName', this.stepName);
    },
    '$store.state.carriers.carrierData': {
      immediate: true,
      handler(val) {
        if (!val) return;

        console.log('Hey');

        const paymentsState = this.$store.state.payments;
        const questionsState = this.$store.state.questions;

        const term = val.payment_term || null;

        if (term?.id > 0) {
          if (paymentsState.payment_term_id !== term.id) {
            this.$store.commit('payments/setPaymentData', {
              data: term.id,
              type: 'payment_term_id',
            });
          }

          if (paymentsState.payment_type !== term.is_type) {
            this.$store.commit('payments/setPaymentData', {
              data: term.is_type,
              type: 'payment_type',
            });
          }

          if (paymentsState.saved_payment_term !== term) {
            this.$store.commit('payments/setPaymentData', {
              data: term,
              type: 'saved_payment_term',
            });
            this.$store.commit('payments/setPaymentData', {
              data: term,
              type: 'selected_payment_term',
            });
          }

          if (paymentsState.latest_allowed_payment_method === null) {
            this.debouncedGetLatestAllowedPaymentMethod(term);
          }
        } else if (paymentsState.saved_payment_term !== null) {
          this.$store.commit('payments/setPaymentData', {
            data: null,
            type: 'saved_payment_term',
          });
        }

        const method = val.payment_method || null;

        if (method?.id > 0) {
          if (paymentsState.payment_source !== method.is_type) {
            this.$store.commit('payments/setPaymentData', {
              data: method.is_type,
              type: 'payment_source',
            });
          }

          if (
            !paymentsState.saved_payment_method ||
            paymentsState.saved_payment_method.id !== method.id
          ) {
            this.$store.commit('payments/setPaymentData', {
              data: method,
              type: 'saved_payment_method',
            });
          }
        } else if (paymentsState.saved_payment_method !== null) {
          this.$store.commit('payments/setPaymentData', {
            data: null,
            type: 'saved_payment_method',
          });
        }

        const newSections = val.company_question_sections?.length || 0;
        if (newSections !== questionsState.total_sections) {
          this.$store.commit('questions/setTotalSections', newSections);
        }
      },
    },
    // '$store.state.carriers.carrierData': {
    //   deep: true,
    //   handler(val) {
    //     console.log('Its called');
    //     // Anything that needs to be updated when the carrierData changes
    //     if (val?.payment_term?.id > 0) {
    //       this.$store.commit('payments/setPaymentData', {
    //         data: val?.payment_term?.id,
    //         type: 'payment_term_id',
    //       });
    //       this.$store.commit('payments/setPaymentData', {
    //         data: val?.payment_term?.is_type,
    //         type: 'payment_type',
    //       });
    //       this.$store.commit('payments/setPaymentData', {
    //         data: val?.payment_term,
    //         type: 'saved_payment_term',
    //       });
    //       this.$store.commit('payments/setPaymentData', {
    //         data: val?.payment_term,
    //         type: 'selected_payment_term',
    //       });

    //       if (
    //         this.$store.state.payments?.latest_allowed_payment_method === null
    //       ) {
    //         this.debouncedGetLatestAllowedPaymentMethod(val?.payment_term);
    //       }
    //     } else {
    //       this.$store.commit('payments/setPaymentData', {
    //         data: null,
    //         type: 'saved_payment_term',
    //       });
    //     }

    //     const filterName = this.shouldEnableCHRFlow
    //       ? 'Integrations'
    //       : 'Ownership';
    //     this.steps = this.steps.filter((step) => step.name !== filterName);

    //     if (val?.payment_method?.id > 0) {
    //       this.$store.commit('payments/setPaymentData', {
    //         data: val?.payment_method?.is_type,
    //         type: 'payment_source',
    //       });
    //       this.$store.commit('payments/setPaymentData', {
    //         data: val?.payment_method,
    //         type: 'saved_payment_method',
    //       });
    //     } else {
    //       this.$store.commit('payments/setPaymentData', {
    //         data: null,
    //         type: 'saved_payment_method',
    //       });
    //     }

    //     if (
    //       val?.company_question_sections?.length !=
    //       this.$store.state.questions?.total_sections
    //     ) {
    //       this.$store.commit(
    //         'questions/setTotalSections',
    //         val?.company_question_sections?.length
    //       );
    //     }
    //   },
    //   immediate: true,
    // },
    latest_company_tax_form_submission(newVal) {
      if (
        newVal?.is_active == true &&
        [
          'w_8ben',
          'w_8ben_e',
          'w_8ben_e_substitute',
          'w_8eci',
          'form_8233',
        ].includes(newVal?.tax_form_is_type) &&
        this.$store.state.firstTime?.Contract == true
      ) {
        let allowed =
          this.$store.state.carriers?.carrierData?.connection?.issuer
            ?.allowed_w8_is_types ||
          this.$store.state.company?.companyData?.allowed_w8_is_types;
        if (allowed && allowed?.length > 0) {
          if (allowed.includes(newVal?.tax_form_is_type)) {
            this.$store.commit('firstTime/updateStepFirstTime', {
              step: 'Contract',
              value: false,
            });
          }
        } else {
          if (newVal?.tax_form_is_type !== 'w_8ben_e_substitute') {
            this.$store.commit('firstTime/updateStepFirstTime', {
              step: 'Contract',
              value: false,
            });
          }
        }
      }
    },
  },

  beforeMount() {
    this.$store.commit('firstTime/updateInitialFirstTime', this.getFirstTime());
  },

  mounted() {
    this.mounted = true;
    this.$store.commit(
      'addFormDebugMessage',
      'BEGINNING ONBOARDING SESSION FOR ONBOARDING PACKET ID ' +
        this.$router?.currentRoute?.value?.params?.id
    );
  },
  methods: {
    emitExitModal() {
      this.$emit('exit-modal');
    },
    formatIsType(type) {
      if (!type) return '';

      return type?.replace('_', ' ');
    },
    // Debounced method to prevent excessive API calls during typing
    debouncedGetLatestAllowedPaymentMethod: debounce(function (payment_term) {
      if (this.$store.state.payments?.latest_allowed_payment_method === null) {
        this.$store.dispatch(
          'payments/getLatestAllowedPaymentMethod',
          payment_term
        );
      }
    }, 1000),
    // TODO: Revisit this function if && !this.firstTime is needed
    async editCurrentStep() {
      if (this.stepName === 'Contract-2') {
        this.skipStep(this.formPosition - 1);
      } else if (this.stepName === 'Payment-2') {
        // if (this.$store.state.payments.payment_type === "factoring") {
        //   this.$store.dispatch("payments/updateNestedField", {
        //     path: "edit.factor",
        //     value: true,
        //   });
        // } else {
        this.skipStep(this.formPosition + 2);
        // }
      } else if (this.stepName === 'Certifications-1') {
        // Move user to 2nd certification step
        // await this.$props.nextStep();
        // this.temporaryEdit = true;
        await this.$store.commit('firstTime/updateStepFirstTime', {
          step: this.currentSectionName,
          value: true,
        });
      } else {
        // update currentstep here -- but go back to second time view on next step
        this.temporaryEdit = true;
        this.$store.commit('firstTime/updateStepFirstTime', {
          step: this.currentSectionName,
          value: true,
        });
      }
    },
    openToast() {
      this.onCloseModal();
      this.error = true;
      this.$toast?.open({
        message: 'Message Sent',
        type: 'success',
        duration: 5000,
        dismissible: true,
        position: 'top-right',
      });
    },
    progressTitleStyles(title) {
      if (title === 'Identity') {
        return 'left: 0;';
      } else if (
        title === 'Payment' &&
        this.stepTitles?.indexOf('Payment') == this.stepTitles?.length - 1
      ) {
        return 'right: -0.25rem;';
      } else if (
        title === 'Questionnaire' &&
        this.stepTitles?.indexOf('Questionnaire') == this.stepTitles?.length - 1
      ) {
        return 'right: -0.25rem;';
      } else if (
        title === 'Integrations' &&
        this.stepTitles?.indexOf('Integrations') == this.stepTitles?.length - 1
      ) {
        return 'right: -0.25rem;';
      } else if (title === 'Contract') {
        return 'right: -0.25rem;';
      } else {
        return 'left: 0.25rem; transform: translate(-50%, 0);';
      }
    },
    async verifyPayment() {
      const isTriumphPayRendered =
        this.$store.state.payments.rendered_payment?.[0]?.is_type ===
          'triumph_pay' ||
        this.$store.state.payments.rendered_payment?.is_type === 'triumph_pay';
      const TriumphPayMethod =
        this.$store.state.payments.rendered_payment?.[0] ||
        this.$store.state.payments.rendered_payment;
      let verification_id = this.$store.state.payments?.verification_id;
      let code = this.$store.state.payments?.verification_code;
      let res = await this.$store.dispatch('payments/verifyPaymentCode', {
        verification_id,
        code,
      });
      if (res.data.status === 'confirmed') {
        this.$store.dispatch('payments/updateNestedField', {
          path: 'errors',
          value: false,
        });
        this.$store.dispatch('payments/updateNestedField', {
          path: 'edit.payment_method',
          value: true,
        });

        if (
          (isTriumphPayRendered ||
            this.$store.state.payments?.latest_payment_method?.[0]?.is_type ===
              'triumph_pay') &&
          this.isTriumphPayAllowedByPaymentTerms
        ) {
          if (
            isTriumphPayRendered &&
            Number(TriumphPayMethod?.triumph_pay_select_carrier_rate) > 0
            // && this.$store.state.payments.payment_type !== 'factoring'
          ) {
            this.skipStep(this.formPosition - 3);
          } else {
            //Triumph Pay flow.
            this.$store.commit('toggleDisabledButton', {
              bool: true,
              source: 'FormFlow #1',
            });
            this.$store.commit('payments/setEditFactor', true);
            this.skipStep(this.formPosition - 2);
            return;
          }

          this.$store.commit('payments/setTriumphPayDeclined', true);
        } else {
          //Normal flows.
          if (this.$store.state.payments?.payment_type === 'factoring') {
            this.$store.commit('toggleDisabledButton', {
              bool: true,
              source: 'FormFlow #2',
            });
            this.$store.commit('payments/setEditFactor', true);
          }

          this.skipStep(this.formPosition - 2);
        }
      } else {
        this.$store.dispatch('payments/updateNestedField', {
          path: 'errors',
          value: true,
        });
      }
    },
    getFirstTime() {
      // Will set to false when condition below is met,
      // otherwise will remain true
      //
      // Identity: Dispatch, billing, and claims contacts are present
      // Operations: At least 1 equipment_type is present
      // Lanes: At least 1 latest_onboarding_lane_preference is present
      // Certifications: At least 1 untracked certification is present
      // Insurance (Certificates step): At least 1 policy is present
      // Insurance (Exclusions step): At least 1 item exclusions
      // Payment: A payment method is present
      // Questions: At least 1 question answer is present
      // Contract: A tax classification and at least 1 tax identifier is present

      const {
        identity,
        operations,
        lanes,
        certifications,
        insurance_policies,
        exclusions,
        payment_method,
        payment_term,
        factor,
      } = this.data;
      let firstTime = {};
      let dispatchContactPresent = false;
      let billingContactPresent = false;
      let claimsContactPresent = false;
      if (identity?.email_addresses?.length > 0) {
        dispatchContactPresent = identity.contacts.some(
          (el) => el.is_type === 'dispatch'
        );
        billingContactPresent = identity.contacts.some(
          (el) => el.is_type === 'billing'
        );
        claimsContactPresent = identity.contacts.some(
          (el) => el.is_type === 'claims'
        );
      }

      firstTime['Identity'] = !(
        dispatchContactPresent &&
        billingContactPresent &&
        claimsContactPresent
      );
      firstTime['Operations'] = !(operations?.equipment_types?.length > 0);
      firstTime['Lanes'] = !lanes?.latest_onboarding_lane_preferences?.length;
      firstTime['Certifications'] = !certifications.some((el) =>
        stepInformation.certifications.untracked_certifications.includes(
          el?.is_type
        )
      );
      firstTime['Insurance'] = !insurance_policies?.length;
      // firstTime["Insurance-Certificates"] = !insurance_policies?.length;
      firstTime['InsuranceExclusions'] = !exclusions?.length;
      firstTime['Payment'] =
        (payment_term?.is_type === 'factoring' && !factor) ||
        (payment_term?.is_type !== 'factoring' && !payment_method);
      firstTime['Questions'] = !this.$store.state.questions?.has_answers;
      firstTime['Contract'] =
        !identity?.tax_identifiers?.length || !identity?.tax_classification;
      // firstTime["Contract"] = !this.latest_company_tax_form_submission?.is_active;

      return firstTime;
    },
  },

  setup() {
    const store = useStore();
    const router = useRouter();
    const toast = useToast();

    const showSentModal = ref(false);

    const showErrorModal = computed(() => store.state.showErrorModal);
    const showNeedHelpModal = computed(() => store.state.showNeedHelpModal);

    const finalButtonLoading = ref(false);

    const existSomeTriumphPayMethod = computed(() =>
      store.state.payments.activePaymentMethods?.some(
        (payment) => payment?.is_type === 'triumph_pay'
      )
    );
    const isTriumphPayAllowedByPaymentTerms = computed(() =>
      store.state.payments?.payment_terms?.some((term) =>
        term?.allowed_payment_methods?.includes('triumph_pay')
      )
    );

    const openErrorModal = () => {
      store.commit('setShowErrorModal', true);
    };
    const onCloseModal = () => {
      store.commit('setShowErrorModal', false);
    };

    const onSuccessError = () => {
      store.commit('setShowErrorModal', false);
      showSentModal.value = true;
    };

    const onCloseSentModal = () => {
      showSentModal.value = false;
      // props.nextStep();
    };

    /**
     * Create a payment method according to the information filled in the form.
     * @param {*} paymentMethod The body of the request to be made.
     * @param {*} onboardingPacketId The ondboarding packet id.
     * @returns Returns true if it was possible to create the payment method or false if it was not possible and there was an error.
     */
    const isPaymentMethodAllowed = (requestedPaymentMethod) => {
      const paymentTerms = store.state.payments?.payment_terms;
      return paymentTerms?.some((payment) =>
        payment?.allowed_payment_methods?.includes(requestedPaymentMethod)
      );
    };

    // Execute the contract for the onboarding packet
    // and complete the packet.  Handle the logic for
    // Directing the user to the next step depending on
    // the company type and the status of the packet
    const executeContractAndCompletePacket = async () => {
      // Execute contract API call
      finalButtonLoading.value = true;
      const executeRes = await store.dispatch(
        'onboardingPacket/executeContract'
      );
      if ([200, 201].includes(executeRes?.status)) {
        switch (store.state.users.currentUser?.company?.company_type) {
          case 'dispatcher':
            // Dispatchers don't follow the OAuth 2 flow, they go to a status
            // Screen for the packet
            console.log('dispatcher completed packet; redirecting to status');

            if (
              store.state.onboardingPacket?.status === 'complete' ||
              store.state.onboardingPacket?.status ===
                'held_for_carrier_approval'
            ) {
              return router.push({ name: 'Dispatcher Dashboard' });
            }
            return router.push({
              name: 'Dispatcher Onboarding Packet Status',
              params: { id: store.state.onboardingPacket?.id },
            });
          case 'broker':
          case 'carrier':
            return completePacketAsCarrier();
        }
      } else {
        console.log('there was an error');
        // There was an error
        toastResponseInSetup(toast, executeRes, null);
        finalButtonLoading.value = false;
      }
    };

    const completePacketAsCarrier = async () => {
      console.log(
        'completing packet as a carrier',
        store.state.onboardingPacket
      );
      if (store.state.onboardingPacket?.status != 'complete') {
        console.log('packet is incomplete; redirect to status screen');
        router.push({
          name: 'Carrier Onboarding Packet Status',
          params: { id: store.state.onboardingPacket?.id },
        });
      } else {
        console.log(
          'packet is complete; redirect to createAuthorization if present'
        );

        // If they came in through the OAuth process,
        // then then they will be redirected to the /oauth/authorizations
        // endpoint with the correct credentials
        let redirectAfterSignIn = JSON.parse(
          localStorage.getItem('redirect_url_after_sigin')
        );
        if (redirectAfterSignIn) {
          localStorage.removeItem('redirect_url_after_sigin');
          return router.push(redirectAfterSignIn);
        } else {
          router.push({
            name: 'Carrier Onboarding Packet Status',
            params: { id: store.state.onboardingPacket?.id },
          });
        }
      }
    };

    return {
      openErrorModal,
      showErrorModal,
      onCloseModal,
      showSentModal,
      showNeedHelpModal,
      onCloseSentModal,
      onSuccessError,
      executeContractAndCompletePacket,
      completePacketAsCarrier,
      finalButtonLoading,
      existSomeTriumphPayMethod,
      isPaymentMethodAllowed,
      isTriumphPayAllowedByPaymentTerms,
    };
  },
};
</script>
<style scoped>
@media screen and (max-width: 768px) {
  .box-shadow-top {
    box-shadow: 0px 0px 25px 0px #6f6f6f80;
  }
}

@media screen and (min-width: 768px) {
  .footer-container {
    margin-bottom: 32px !important;
    bottom: 16px !important;
  }
}

.paragraph {
  font-size: 10px;
}

@media screen and (max-width: 680px) {
  .button-container-mobile {
    position: fixed;
    bottom: 0px;
    width: 100%;
  }
  .content-container {
    margin-bottom: 233px;
  }
}
</style>
